import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { setServerErrors, useMutation } from '../../Shared/api';
import { useNotifier } from '../../Shared/Notifier/NotificationProvider';
import { Button } from '../../Shared/UI/Button';
import ErrorMessage from '../../Shared/UI/ErrorMessage';
import { Input } from '../../Shared/UI/Input';
import InputGroup from '../../Shared/UI/InputGroup';
import InputLabel from '../../Shared/UI/InputLabel';
import ServerErrors from '../../Shared/UI/ServerErrors';
import { useRouter } from '../router';

export default function AddUserForm({
  onSuccess,
}: {
  onSuccess?: () => void;
}) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const notifier = useNotifier();

  const { register, handleSubmit, setError, formState: { errors, defaultValues } } = useForm({
    defaultValues: {
      email: '',
      first_name: '',
      last_name: '',
    },
  });

  const [addUser, { loading, errors: serverErrors }] = useMutation(routes.add_user(), {
    onSuccess: () => {
      notifier.notify(t('backend:user_added'));
      onSuccess?.();
    },
    onError: (errors) => {
      setServerErrors(errors, setError, defaultValues);
    },
  });

  return (
    <form onSubmit={handleSubmit(addUser)}>
      <div className="space-y-6">
        <div className="space-y-2">
          <h3 className="py-1">
            {t('backend:add_a_user')}
          </h3>
          <p className="text-slate-700">
            {t('backend:add_user_intro')}
          </p>
        </div>

        <hr />

        {serverErrors && <ServerErrors errors={serverErrors} defaultValues={defaultValues} />}

        <InputGroup>
          <InputLabel required valid={!errors.email} htmlFor="add_user_email">
            {t('backend:email_address')}
          </InputLabel>
          <Input
            {...register('email', { required: true })} inputMode="email"
            id="add_user_email"
            aria-invalid={!!errors.email}
          />
          <ErrorMessage attribute={t('backend:email_address')} error={errors.email }/>
        </InputGroup>

        <div className="grid grid-cols-2 gap-6">
          <InputGroup>
            <InputLabel required valid={!errors.first_name} htmlFor="add_user_first_name">
              {t('backend:first_name')}
            </InputLabel>
            <Input
              {...register('first_name', { required: true })}
              id="add_user_first_name"
              aria-invalid={!!errors.first_name}
            />
            <ErrorMessage attribute={t('backend:first_name')} error={errors.first_name }/>
          </InputGroup>

          <InputGroup>
            <InputLabel required valid={!errors.last_name} htmlFor="add_user_last_name">
              {t('backend:last_name')}
            </InputLabel>
            <Input
              {...register('last_name', { required: true })}
              id="add_user_last_name"
              aria-invalid={!!errors.last_name}
            />
            <ErrorMessage attribute={t('backend:last_name')} error={errors.last_name }/>
          </InputGroup>
        </div>

        <div className="text-right">
          <Button
            type="submit"
            disabled={loading}
            loading={loading}
          >
            {t('backend:add_user')}
          </Button>
        </div>
      </div>
    </form>
  );
}
